import React from "react";
import {
  UserPlus, CalendarCheck, Phone, Wallet, PiggyBank, Handshake,
  Megaphone, MonitorSmartphone, GraduationCap, FileCheck2,
  Users, LayoutDashboard, BadgeDollarSign, Sparkles,
} from "lucide-react";
import { usePageMeta } from "@/components/site/usePageMeta";
import { PageHero } from "@/components/site/PageHero";
import { SectionHeading } from "@/components/site/ui";
import { Reveal } from "@/components/site/useReveal";
import { RecruitingSection } from "@/components/site/RecruitingSection";
import { LeadForm } from "@/components/site/LeadForm";
import { ContactSection } from "@/components/site/ContactSection";
import { CONTACT, ROUTES } from "@/components/site/data";

const IMG = "https://images.pexels.com/photos/8730012/pexels-photo-8730012.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1000";

// Why agents join — the core recruiting pitch.
const PILLARS = [
  {
    icon: Wallet,
    title: "Keep More of Every Deal",
        text: "A 100%-commission style model built so top producers and growing agents keep more of what they earn, instead of feeding a legacy split.",
  },
  {
    icon: PiggyBank,
    title: "Simple, Transparent Fees",
    text: "A low monthly fee and a flat per-transaction fee. No franchise fees, no desk fees, no surprise deductions on your settlement statement.",
  },
  {
    icon: Handshake,
    title: "Broker Support & Mentorship",
        text: "Direct access to broker John Pagani for contracts, negotiations, compliance, and deal strategy, real answers when a deal is on the line.",
  },
  {
    icon: Megaphone,
    title: "Marketing Support",
    text: "Listing marketing, social templates, photography guidance, and brand assets that make every listing and every agent look premium.",
  },
  {
    icon: MonitorSmartphone,
    title: "CRM & Tech Stack",
        text: "A modern CRM with lead routing, automated follow-up, IDX-powered home search, and pipeline tracking. Your business, organized.",
  },
  {
    icon: GraduationCap,
    title: "Training & Resources",
    text: "Onboarding for newer agents, sharpening for veterans: scripts, contract training, buyer/seller systems, and mortgage fluency.",
  },
  {
    icon: FileCheck2,
    title: "Transaction Support",
    text: "Transaction coordination support from accepted offer to close, so you can stay in production instead of chasing paperwork.",
  },
  {
    icon: Users,
    title: "Lead Opportunities",
    text: "Company-generated buyer and seller leads, sign calls, and IDX registrations shared with agents who are ready to work them.",
  },
  {
    icon: LayoutDashboard,
    title: "Modern Agent Portal",
        text: "One login for your files, commission tracking, marketing assets, and training library, accessible from any device.",
  },
];

const STEPS = [
  { n: "01", title: "Apply or Book a Call", text: "Tell us about your license, experience, and goals, or go straight to the calendar." },
  { n: "02", title: "Meet With the Broker", text: "A direct, no-pressure conversation about the model, the fees, and whether we're the right fit for your business." },
  { n: "03", title: "Onboard & Launch", text: "License transfer, CRM setup, marketing assets, and your first-week game plan. Most agents are live within days." },
];

export default function JoinPage() {
  usePageMeta(
    "Join Wallstreet Realty | 100% Commission-Style Brokerage for CA Agents",
    "Licensed California agents: keep more of your commission with transparent flat fees, broker mentorship, marketing, CRM, leads, and transaction support at Wallstreet Realty."
  );
  return (
    <>
      <PageHero
        crumb="Join Wallstreet Realty"
        eyebrow="Careers · For Licensed Agents"
        title="Your Business. Your Commission. Our Support."
                subtitle="Wallstreet Realty is an agent-first Southern California brokerage: a 100%-commission style model with transparent flat fees, backed by real broker support, marketing, technology, and leads."
        image={IMG}
        ctas={[
          { label: "Apply to Join", href: "#apply", variant: "gold", testid: "join-hero-apply" },
          { label: "Book a Call", href: "#book-call", variant: "outline", testid: "join-hero-call" },
        ]}
      />

      {/* Why Join — 9 pillars */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why Join Wallstreet Realty"
            title="Built Around the Agent, Not the Franchise"
            subtitle="Most brokerages ask what you can do for them. We flipped it: keep more of your commission, get real support, and grow a business you actually own."
            center
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 40}>
                <div
                  data-testid={`join-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="lift h-full bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-7 hover:border-[#C9A961]"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#254369]/10 flex items-center justify-center mb-4">
                    <p.icon className="text-[#254369]" size={20} />
                  </div>
                  <h3 className="font-heading font-bold text-[#2D2D37] text-lg">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#2D2D37]/65">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Commission model */}
      <section className="py-16 lg:py-24 bg-[#254369]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#C9A961] font-heading font-bold uppercase tracking-[0.18em] text-xs mb-4">
                The Model
              </p>
              <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl leading-[1.1] text-white">
                Keep Your Commission. Know Your Numbers.
              </h2>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/80">
                Traditional splits quietly take tens of thousands from a
                producing agent every year. Wallstreet Realty runs a
                100%-commission style structure: a low monthly fee, a flat
                transaction fee, and that's it. Every fee is disclosed up
                                front. We'll walk you through the exact numbers on your call
                and show you what you'd have kept last year with us.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                        "Low monthly fee, not a split that scales against you",
                  "Flat per-transaction fee, disclosed before you join",
                  "No franchise fees, no desk fees, no hidden deductions",
                  "E&O and compliance handled the right way",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-white/85">
                    <BadgeDollarSign size={18} className="mt-0.5 shrink-0 text-[#C9A961]" />
                    <span className="text-[15px] leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Reveal>
              <div className="bg-white/[0.06] border border-white/10 rounded-[20px] p-8 sm:p-10">
                <Sparkles className="text-[#C9A961]" size={28} />
                <h3 className="mt-4 font-heading font-bold text-white text-2xl leading-snug">
                  Do the math on your last 12 months.
                </h3>
                <p className="mt-3 text-white/75 leading-relaxed text-[15px]">
                  Bring your production to the recruiting call. We'll compare
                  your current split against our flat-fee structure, line by
                                                      line. Most agents are surprised by the difference.
                </p>
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  data-testid="join-model-call"
                  className="mt-7 inline-flex items-center gap-2 bg-[#C9A961] text-white rounded-lg px-6 py-3 font-medium hover:bg-[#b09252] transition-colors"
                >
                          <Phone size={17} /> Call the Broker: {CONTACT.phone}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Existing brand strip with 8 support cards */}
      <RecruitingSection />

      {/* How joining works */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="How It Works" title="Three Steps to Your Next Chapter" center />
          <div className="mt-12 grid sm:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 60}>
                <div className="h-full bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-7">
                  <span className="font-heading font-extrabold text-4xl text-[#C9A961]">{s.n}</span>
                  <h3 className="mt-4 font-heading font-bold text-lg text-[#2D2D37]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#2D2D37]/65">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Apply + Book a Call */}
      <section id="apply" className="py-16 lg:py-24 bg-[#F5F5F5] scroll-mt-[70px]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Apply Now"
            title="Apply to Join Wallstreet Realty"
            subtitle="Tell us about yourself and the broker will follow up personally about the model, support, and exact fee structure."
            center
          />
          <div className="mt-10 grid lg:grid-cols-2 gap-6 items-start">
            <LeadForm variant="agent" testid="agent-application-form" />
            <div id="book-call" className="scroll-mt-[90px]">
              <div className="bg-white border border-[#E5E5E5] rounded-[20px] p-6 sm:p-8 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-[#254369]/10 flex items-center justify-center mb-4">
                  <CalendarCheck className="text-[#254369]" size={22} />
                </div>
                <p className="font-heading font-bold text-xl text-[#2D2D37]">Book a Recruiting Call</p>
                <p className="mt-2 text-sm leading-relaxed text-[#2D2D37]/65">
                  Prefer to talk first? Call the broker directly, or request a
                  call back and we'll reach out at the time that works for you.
                </p>
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  data-testid="join-book-call-phone"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#254369] text-white rounded-xl px-6 py-3.5 font-medium hover:bg-[#2D2D37] transition-colors"
                >
                  <Phone size={17} /> Call {CONTACT.phone}
                </a>
                <div className="mt-6 border-t border-[#E5E5E5] pt-6">
                  <LeadForm variant="call" testid="recruiting-call-form" />
                </div>
              </div>
            </div>
          </div>
          <p className="mt-8 text-center text-xs text-[#2D2D37]/45 flex items-center justify-center gap-2">
                        <UserPlus size={14} /> For licensed California real estate agents. Not licensed yet? Reach out. We'll point you in the right direction.
          </p>
        </div>
      </section>

      <ContactSection />
    </>
  );
}
